import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AdvancedSettingsDialogComponent, AdvancedSettingsData } from './advanced-settings-dialog.component';
import { ConfigurationService } from '../../services/configuration.service';

@Component({
  selector: 'app-commission-split',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatDialogModule
  ],
  templateUrl: './commission-split.component.html',
  styleUrl: './commission-split.component.css'
})
export class CommissionSplitComponent implements OnInit {
  private dialog = inject(MatDialog);
  private configService = inject(ConfigurationService);
  
  // Input signals - will be loaded from configuration
  yearOneSales = signal(100);
  avgMonthlyPrice = signal(82);
  monthlyCost = signal(15);
  retentionRate = signal(85); // 85% retention rate
  
  // UI state signals - removed showAdvancedSettings since it's now handled by modal
  
  // Multi-year data
  additionalYears = signal<Array<{sales: number, avgPrice: number}>>([]);

  async ngOnInit(): Promise<void> {
    await this.loadConfigurationValues();
  }

  /**
   * Load default values from configuration system
   */
  private async loadConfigurationValues(): Promise<void> {
    try {
      const businessMetrics = await this.configService.getBusinessMetrics();
      
      // Update signals with configuration values
      this.yearOneSales.set(businessMetrics.yearOneSales);
      this.avgMonthlyPrice.set(businessMetrics.avgMonthlyPrice);
      this.monthlyCost.set(businessMetrics.monthlyCost);
      this.retentionRate.set(businessMetrics.retentionRate);
      
      console.log('✅ Loaded commission split configuration:', businessMetrics);
    } catch (error) {
      console.warn('⚠️ Could not load configuration values, using defaults:', error);
      // Keep the existing default values if configuration loading fails
    }
  }

  // Computed signals for calculations
  annualRevenue = computed(() => {
    return this.yearOneSales() * this.avgMonthlyPrice() * 12;
  });

  annualCosts = computed(() => {
    return this.yearOneSales() * this.monthlyCost() * 12;
  });

  annualProfit = computed(() => {
    return this.annualRevenue() - this.annualCosts();
  });

  yearOneCommission = computed(() => {
    return Math.max(0, this.annualProfit() * 0.5);
  });

  // Multi-year calculations
  totalCommissionAllYears = computed(() => {
    let total = this.yearOneCommission();
    let previousYearCustomers = this.yearOneSales();
    
    this.additionalYears().forEach((year, index) => {
      // Calculate retained customers from previous year
      const retainedCustomers = Math.floor(previousYearCustomers * (this.retentionRate() / 100));
      
      // Total customers = retained + new sales
      const totalCustomers = retainedCustomers + year.sales;
      
      const revenue = totalCustomers * year.avgPrice * 12;
      const costs = totalCustomers * this.monthlyCost() * 12;
      const profit = revenue - costs;
      const commission = Math.max(0, profit * 0.5);
      total += commission;
      
      // Update for next iteration
      previousYearCustomers = totalCustomers;
    });
    
    return total;
  });

  totalYears = computed(() => {
    return 1 + this.additionalYears().length;
  });

  // Getter methods for template binding
  get yearOneSalesValue() {
    return this.yearOneSales();
  }

  set yearOneSalesValue(value: number) {
    this.yearOneSales.set(value);
  }

  get avgMonthlyPriceValue() {
    return this.avgMonthlyPrice();
  }

  set avgMonthlyPriceValue(value: number) {
    this.avgMonthlyPrice.set(value);
  }

  // Toggle advanced settings
  toggleAdvancedSettings() {
    const dialogRef = this.dialog.open(AdvancedSettingsDialogComponent, {
      width: '500px',
      data: {
        monthlyCost: this.monthlyCost(),
        retentionRate: this.retentionRate()
      }
    });

    dialogRef.afterClosed().subscribe((result: AdvancedSettingsData | undefined) => {
      if (result) {
        this.monthlyCost.set(result.monthlyCost);
        this.retentionRate.set(result.retentionRate);
      }
    });
  }

  // Multi-year management
  addYear() {
    const currentYears = this.additionalYears();
    const newYear = {
      sales: this.yearOneSales(), // Default to same as year one
      avgPrice: this.avgMonthlyPrice() // Default to same as year one
    };
    this.additionalYears.set([...currentYears, newYear]);
  }

  removeYear(index: number) {
    const currentYears = this.additionalYears();
    const updatedYears = currentYears.filter((_, i) => i !== index);
    this.additionalYears.set(updatedYears);
  }

  updateYearSales(index: number, sales: number) {
    const currentYears = this.additionalYears();
    const updatedYears = [...currentYears];
    updatedYears[index] = { ...updatedYears[index], sales };
    this.additionalYears.set(updatedYears);
  }

  updateYearPrice(index: number, avgPrice: number) {
    const currentYears = this.additionalYears();
    const updatedYears = [...currentYears];
    updatedYears[index] = { ...updatedYears[index], avgPrice };
    this.additionalYears.set(updatedYears);
  }

  getYearCommission(year: {sales: number, avgPrice: number}, yearIndex: number): number {
    // Calculate retained customers from previous years
    let previousYearCustomers = this.yearOneSales();
    
    // Calculate retained customers for this specific year
    for (let i = 0; i < yearIndex; i++) {
      const retainedFromPrevious = Math.floor(previousYearCustomers * (this.retentionRate() / 100));
      const additionalYear = this.additionalYears()[i];
      previousYearCustomers = retainedFromPrevious + additionalYear.sales;
    }
    
    const retainedCustomers = Math.floor(previousYearCustomers * (this.retentionRate() / 100));
    const totalCustomers = retainedCustomers + year.sales;
    
    const revenue = totalCustomers * year.avgPrice * 12;
    const costs = totalCustomers * this.monthlyCost() * 12;
    const profit = revenue - costs;
    return Math.max(0, profit * 0.5);
  }

  getYearTotalCustomers(year: {sales: number, avgPrice: number}, yearIndex: number): number {
    let previousYearCustomers = this.yearOneSales();
    
    for (let i = 0; i < yearIndex; i++) {
      const retainedFromPrevious = Math.floor(previousYearCustomers * (this.retentionRate() / 100));
      const additionalYear = this.additionalYears()[i];
      previousYearCustomers = retainedFromPrevious + additionalYear.sales;
    }
    
    const retainedCustomers = Math.floor(previousYearCustomers * (this.retentionRate() / 100));
    return retainedCustomers + year.sales;
  }

  async contactRecruiter(): Promise<void> {
    try {
      const companyInfo = await this.configService.getCompanyInfo();
      const commissionRates = await this.configService.getCommissionRates();
      
      // Use configured recruiting email and commission rate for subject
      const commissionPercentage = Math.round(commissionRates.longTerm * 100);
      const subject = `Door-to-Door Sales Opportunity - ${commissionPercentage}% Commission`;
      
      window.location.href = `mailto:${companyInfo.recruitingEmail}?subject=${encodeURIComponent(subject)}`;
    } catch (error) {
      console.warn('⚠️ Could not load configuration for recruiting email, using defaults:', error);
      // Fallback to hardcoded values
      window.location.href = 'mailto:recruiting@completepestcontrol.pro?subject=Door-to-Door Sales Opportunity - 50% Commission';
    }
  }

  downloadInfo() {
    // This would typically download a PDF or open a new window with detailed info
    alert('Info sheet download feature coming soon! Please contact our recruiter for detailed information about our 50% profit sharing program.');
  }
}